// Motor de bitácora UCI (Fase 4, Tanda 4 + fix de Tanda 5a, ver PLAN.md) —
// `GraficoEnfermeria`: el gráfico de línea "hoja de enfermería" (24h del
// mismo turno 8→7 que `TablaHoraria24h`, con un símbolo distinto por serie
// superpuesto) — geometría fija del papel impreso + captura de puntos,
// mismo criterio que Denver II/odontograma/partograma (bespoke, no el motor
// `recharts` de Fase 1).
//
// Escala por serie (no una escala global compartida): el gráfico real de
// constantes de 113 superpone 4 series con 4 escalas numéricas DISTINTAS
// sobre las mismas líneas de grilla (FR 20-90, T° 33-41, Pulso 20-180,
// TA 40-200 — confirmado leyendo la página 220 del PDF fuente al conectar
// 113, Tanda 5a). Símbolo por punto: FR cambia de símbolo según el modo
// ventilatorio del punto (○ espontánea/asistida, ● controlada), no un
// símbolo fijo por serie — por eso una serie puede declarar `variantes` y
// cada punto puede llevar su propia `variante`, sin afectar a T/P/TA que
// siguen usando el símbolo fijo de su serie.
//
// Spike original (Tanda 4): las series/escalas son genéricas, definidas por
// quien use el componente — el catálogo real de 114 (temperatura/TA/pulso/
// FR) sigue pendiente de transcribir al conectar ese formulario.
// Simplificación deliberada, mismo criterio que `PartogramaVisual`: un
// punto por hora de registro por serie (clic dentro de la columna de esa
// hora, el valor se interpola de la posición vertical del clic contra la
// escala de la serie activa) — no coordenadas libres.
import { type JSX, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { COLUMNAS_HORARIAS_UCI, type ColumnaHorariaUci } from './TablaHoraria24h';

export type SimboloGraficoEnfermeria = 'circulo' | 'circuloHueco' | 'triangulo' | 'cruz' | 'cuadrado';

export interface EscalaGraficoEnfermeria {
  readonly min: number;
  readonly max: number;
}

/** Variante de símbolo seleccionable para un punto — ej. modo ventilatorio de FR (espontánea/asistida/controlada). Si la serie no declara `variantes`, todos sus puntos usan `simbolo` fijo. */
export interface VarianteSimboloSerie {
  readonly clave: string;
  readonly etiqueta: string;
  readonly simbolo: SimboloGraficoEnfermeria;
}

export interface SerieGraficoEnfermeria {
  readonly clave: string;
  readonly etiqueta: string;
  readonly simbolo: SimboloGraficoEnfermeria;
  readonly color: string;
  readonly escala: EscalaGraficoEnfermeria;
  readonly variantes?: readonly VarianteSimboloSerie[];
}

export interface PuntoGraficoEnfermeria {
  readonly valor: number;
  /** Clave de una de `SerieGraficoEnfermeria.variantes` — `undefined` usa el símbolo fijo de la serie. */
  readonly variante?: string;
}

export type PuntosGraficoEnfermeria = Readonly<Record<string, Readonly<Partial<Record<ColumnaHorariaUci, PuntoGraficoEnfermeria>>>>>;

export function estadoInicialGraficoEnfermeria(series: readonly SerieGraficoEnfermeria[]): PuntosGraficoEnfermeria {
  return Object.fromEntries(series.map((s) => [s.clave, {}]));
}

/** Fija (o borra, con `punto: undefined`) el punto de una serie en una hora — pura, exportada para test. */
export function actualizarPuntoGraficoEnfermeria(
  estado: PuntosGraficoEnfermeria,
  serieClave: string,
  columna: ColumnaHorariaUci,
  punto: PuntoGraficoEnfermeria | undefined,
): PuntosGraficoEnfermeria {
  const puntosSerie = { ...estado[serieClave] };
  if (punto === undefined) {
    delete puntosSerie[columna];
  } else {
    puntosSerie[columna] = punto;
  }
  return { ...estado, [serieClave]: puntosSerie };
}

/** Posición Y (px desde abajo del área de dibujo) de un valor en la escala de SU serie — recortado a [min,max]. Exportada para test junto con su inversa. */
export function yDeValor(valor: number, escala: EscalaGraficoEnfermeria, altoGrafico: number): number {
  const v = Math.min(Math.max(valor, escala.min), escala.max);
  if (escala.max === escala.min) return 0;
  return ((v - escala.min) / (escala.max - escala.min)) * altoGrafico;
}

/** Inversa de `yDeValor` — convierte una coordenada de clic (px desde abajo) al valor de la escala que representa, recortado a [min,max]. */
export function valorDeY(y: number, escala: EscalaGraficoEnfermeria, altoGrafico: number): number {
  if (altoGrafico === 0) return escala.min;
  const fraccion = Math.min(Math.max(y / altoGrafico, 0), 1);
  return escala.min + fraccion * (escala.max - escala.min);
}

/** Símbolo efectivo de un punto: el de su variante si tiene una válida, si no el fijo de la serie. */
export function simboloDePunto(serie: SerieGraficoEnfermeria, punto: PuntoGraficoEnfermeria): SimboloGraficoEnfermeria {
  return serie.variantes?.find((v) => v.clave === punto.variante)?.simbolo ?? serie.simbolo;
}

const ANCHO_COLUMNA = 26;
const CONTENT_W = ANCHO_COLUMNA * COLUMNAS_HORARIAS_UCI.length;
const CONTENT_H = 180;

function xDeColumna(columna: ColumnaHorariaUci): number {
  return COLUMNAS_HORARIAS_UCI.indexOf(columna) * ANCHO_COLUMNA + ANCHO_COLUMNA / 2;
}

function Simbolo({ tipo, cx, cy, color }: { readonly tipo: SimboloGraficoEnfermeria; readonly cx: number; readonly cy: number; readonly color: string }): JSX.Element {
  switch (tipo) {
    case 'circulo':
      return <circle cx={cx} cy={cy} r={4} fill={color} />;
    case 'circuloHueco':
      return <circle cx={cx} cy={cy} r={4} fill="none" stroke={color} strokeWidth={1.5} />;
    case 'cuadrado':
      return <rect x={cx - 3.5} y={cy - 3.5} width={7} height={7} fill={color} />;
    case 'cruz':
      return (
        <g stroke={color} strokeWidth={1.5}>
          <line x1={cx - 4} y1={cy - 4} x2={cx + 4} y2={cy + 4} />
          <line x1={cx - 4} y1={cy + 4} x2={cx + 4} y2={cy - 4} />
        </g>
      );
    case 'triangulo':
      return <polygon points={`${cx},${cy - 5} ${cx - 5},${cy + 4} ${cx + 5},${cy + 4}`} fill={color} />;
  }
}

export interface GraficoEnfermeriaProps {
  readonly series: readonly SerieGraficoEnfermeria[];
  readonly puntos: PuntosGraficoEnfermeria;
  readonly onChange: (puntos: PuntosGraficoEnfermeria) => void;
  readonly serieActiva: string;
  readonly onChangeSerieActiva: (clave: string) => void;
  /** Clave de la variante a aplicar al próximo punto que se marque — ignorada si la serie activa no declara `variantes`. */
  readonly varianteActiva?: string;
  readonly onChangeVarianteActiva?: (clave: string) => void;
}

/** Gráfico de línea "hoja de enfermería" genérico — cada serie con su propia escala, un símbolo fijo (o variable por punto, vía `variantes`) — reusable por el gráfico de constantes de 113 y los de temperatura/TA-pulso-FR de 114/115. */
export function GraficoEnfermeria({ series, puntos, onChange, serieActiva, onChangeSerieActiva, varianteActiva, onChangeVarianteActiva }: GraficoEnfermeriaProps): JSX.Element {
  const { t } = useTranslation();
  const serieActivaDef = series.find((s) => s.clave === serieActiva);

  function manejarClic(columna: ColumnaHorariaUci, offsetYDesdeArriba: number): void {
    if (!serieActivaDef) return;
    const yDesdeAbajo = CONTENT_H - offsetYDesdeArriba;
    const valor = Math.round(valorDeY(yDesdeAbajo, serieActivaDef.escala, CONTENT_H));
    const actual = puntos[serieActiva]?.[columna];
    const nuevo: PuntoGraficoEnfermeria = { valor, variante: varianteActiva };
    const esElMismo = actual !== undefined && actual.valor === nuevo.valor && actual.variante === nuevo.variante;
    onChange(actualizarPuntoGraficoEnfermeria(puntos, serieActiva, columna, esElMismo ? undefined : nuevo));
  }

  const seriesConPuntos = useMemo(
    () =>
      series.map((serie) => ({
        serie,
        puntos: COLUMNAS_HORARIAS_UCI.filter((c) => puntos[serie.clave]?.[c] !== undefined).map((c) => ({ columna: c, punto: puntos[serie.clave]![c]! })),
      })),
    [series, puntos],
  );

  return (
    <div className="flex flex-col gap-3 rounded-lg border border-border bg-hce-surface p-3">
      <div className="flex flex-wrap gap-3">
        <div className="flex flex-col gap-1 sm:w-64">
          <Label htmlFor="grafico-enfermeria-serie-activa">{t('formularios.uci.graficoEnfermeria.serieActiva')}</Label>
          <Select value={serieActiva} onValueChange={onChangeSerieActiva}>
            <SelectTrigger id="grafico-enfermeria-serie-activa">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {series.map((s) => (
                <SelectItem key={s.clave} value={s.clave}>
                  {s.etiqueta}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {serieActivaDef?.variantes && onChangeVarianteActiva && (
          <div className="flex flex-col gap-1 sm:w-64">
            <Label htmlFor="grafico-enfermeria-variante-activa">{t('formularios.uci.graficoEnfermeria.varianteActiva')}</Label>
            <Select value={varianteActiva} onValueChange={onChangeVarianteActiva}>
              <SelectTrigger id="grafico-enfermeria-variante-activa">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {serieActivaDef.variantes.map((v) => (
                  <SelectItem key={v.clave} value={v.clave}>
                    {v.etiqueta}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <div className="relative" style={{ width: CONTENT_W, height: CONTENT_H + 16 }}>
          <button
            type="button"
            className="relative block cursor-crosshair border-0 bg-transparent p-0"
            style={{ width: CONTENT_W, height: CONTENT_H }}
            aria-label={t('formularios.uci.graficoEnfermeria.superficieClicAria')}
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              const y = e.clientY - rect.top;
              const columna = COLUMNAS_HORARIAS_UCI[Math.min(Math.max(Math.floor(x / ANCHO_COLUMNA), 0), COLUMNAS_HORARIAS_UCI.length - 1)];
              manejarClic(columna, y);
            }}
          >
            <svg className="absolute" width={CONTENT_W} height={CONTENT_H} viewBox={`0 0 ${CONTENT_W} ${CONTENT_H}`}>
              <rect x={0} y={0} width={CONTENT_W} height={CONTENT_H} fill="none" stroke="var(--border)" strokeWidth={0.5} />
              {COLUMNAS_HORARIAS_UCI.map((c, i) => (
                <line key={c} x1={i * ANCHO_COLUMNA} y1={0} x2={i * ANCHO_COLUMNA} y2={CONTENT_H} stroke="var(--border)" strokeWidth={0.35} />
              ))}
              {seriesConPuntos.map(({ serie, puntos: pts }) => (
                <g key={serie.clave}>
                  {pts.length > 1 && (
                    <polyline
                      points={pts.map((p) => `${xDeColumna(p.columna)},${CONTENT_H - yDeValor(p.punto.valor, serie.escala, CONTENT_H)}`).join(' ')}
                      fill="none"
                      stroke={serie.color}
                      strokeWidth={1}
                    />
                  )}
                  {pts.map((p) => (
                    <Simbolo key={p.columna} tipo={simboloDePunto(serie, p.punto)} cx={xDeColumna(p.columna)} cy={CONTENT_H - yDeValor(p.punto.valor, serie.escala, CONTENT_H)} color={serie.color} />
                  ))}
                </g>
              ))}
            </svg>
          </button>
          {COLUMNAS_HORARIAS_UCI.map((c, i) => (
            <span key={c} className="absolute -translate-x-1/2 text-[9px] text-hce-muted" style={{ left: i * ANCHO_COLUMNA + ANCHO_COLUMNA / 2, top: CONTENT_H + 2 }}>
              {c}
            </span>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-hce-muted">
        {series.map((s) => (
          <span key={s.clave} className="flex items-center gap-1.5">
            <svg width={12} height={12} viewBox="0 0 12 12">
              <Simbolo tipo={s.simbolo} cx={6} cy={6} color={s.color} />
            </svg>
            {t('formularios.uci.graficoEnfermeria.escalaSerie', { etiqueta: s.etiqueta, min: s.escala.min, max: s.escala.max })}
          </span>
        ))}
      </div>
    </div>
  );
}
